import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

/** Button дизайн-системы: primary — главное действие формы, danger — необратимое. */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:border-transparent disabled:bg-surface-strong disabled:text-muted',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary hover:bg-primary-hover',
        secondary: 'border-control-border bg-canvas text-ink hover:bg-surface-soft',
        ghost: 'text-ink hover:bg-surface-card',
        danger: 'bg-danger text-on-danger hover:bg-danger-hover',
      },
      size: {
        md: 'h-11 px-4 md:h-10',
        // Крупная цель нажатия для «Сканировать» на планшете.
        lg: 'h-12 px-5 text-base',
        sm: 'h-8 rounded-sm px-3',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** Действие выполняется: индикатор и кнопка выключена. */
    busy?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  busy = false,
  disabled,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...props}
    >
      {busy && (
        <span
          aria-hidden="true"
          className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
        />
      )}
      {children}
    </button>
  );
}
